import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { useProperty } from "../lib/PropertyContext";
import { HandbookHeader } from "../components/handbook/HandbookHeader";
import { WifiSection } from "../components/handbook/WifiSection";
import { TextSection } from "../components/handbook/TextSection";
import { EmergencyContactsSection } from "../components/handbook/EmergencyContactsSection";
import { NeighborsSection } from "../components/handbook/NeighborsSection";
import { ShareGuestDialog } from "../components/handbook/ShareGuestDialog";

let __rowUid = 0;
const _uid = () => `row-${Date.now()}-${++__rowUid}`;

export default function CustomerHandbook() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  // Feb 2026 · 8 Sep 2026 · Objekt-Umschalter global synchron. Der Handbook-
  // Header hatte bisher einen eigenen Selector — den ersetzen wir durch den
  // globalen Kontext, damit Wechsel im Dashboard / Settings auch hier
  // reflektiert werden. `HandbookHeader` kriegt `setSelected` aus dem
  // Context-Setter zugereicht.
  const { properties, selected, setSelectedProperty } = useProperty();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showShare, setShowShare] = useState(false);

  const readOnly = user?.role === "family";
  const de = lang === "de";

  const reloadSelected = async () => {
    // Wenn der Server das Property nach dem Save zurueckliefert, muessen wir
    // den globalen Context refreshen. Da das Netz-Fetch dort schon per
    // Interval laeuft, reicht ein passiver Wait — alternativ koennten wir
    // `reload()` aufrufen (siehe Context).
  };

  const startEdit = () => {
    setForm({
      wifi_ssid: selected.wifi_ssid || "",
      wifi_password: selected.wifi_password || "",
      heating_instructions: selected.heating_instructions || "",
      waste_schedule: selected.waste_schedule || "",
      key_location: selected.key_location || "",
      handbook_notes: selected.handbook_notes || "",
      emergency_contacts: (selected.emergency_contacts || []).map((c) => ({ ...c, _uid: _uid() })),
      neighbors: (selected.neighbors || []).map((n) => ({ ...n, _uid: _uid() })),
    });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/properties/${selected.id}`, {
        ...form,
        emergency_contacts: form.emergency_contacts
          .filter((c) => c.label && c.phone)
          .map(({ _uid, ...c }) => c),
        neighbors: form.neighbors
          .filter((n) => n.name)
          .map(({ _uid, ...n }) => n),
      });
      toast.success(de ? "Handbuch gespeichert." : "Handbook saved.");
      // Context-Poll (alle 10 s) zieht den frischen Datensatz automatisch nach.
      setEditing(false);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  if (!selected) {
    return <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-14 label-caps">{t.common.loading}</div>;
  }

  return (
    <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="customer-handbook">
      <HandbookHeader
        de={de}
        properties={properties}
        selected={selected}
        setSelected={setSelectedProperty}
        readOnly={readOnly}
        editing={editing}
        onEdit={startEdit}
        onCancel={() => setEditing(false)}
        onSave={save}
        saving={saving}
        onShare={() => setShowShare(true)}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="handbook-grid">
        <WifiSection de={de} editing={editing} form={form} setForm={setForm} selected={selected} />

        <TextSection
          title={de ? "Heizungsanleitung" : "Heating instructions"}
          testid="handbook-heating"
          editing={editing}
          value={editing ? form?.heating_instructions : selected.heating_instructions}
          onChange={(v) => setForm({ ...form, heating_instructions: v })}
          rows={4}
        />

        <TextSection
          title={de ? "Müllplan" : "Waste schedule"}
          testid="handbook-waste"
          editing={editing}
          value={editing ? form?.waste_schedule : selected.waste_schedule}
          onChange={(v) => setForm({ ...form, waste_schedule: v })}
        />

        <TextSection
          title={de ? "Schlüssel" : "Keys"}
          testid="handbook-keys"
          editing={editing}
          value={editing ? form?.key_location : selected.key_location}
          onChange={(v) => setForm({ ...form, key_location: v })}
        />

        <EmergencyContactsSection de={de} editing={editing} form={form} setForm={setForm} selected={selected} />
        <NeighborsSection de={de} editing={editing} form={form} setForm={setForm} selected={selected} />

        <TextSection
          title={de ? "Weitere Hinweise" : "Additional notes"}
          testid="handbook-notes"
          editing={editing}
          value={editing ? form?.handbook_notes : selected.handbook_notes}
          onChange={(v) => setForm({ ...form, handbook_notes: v })}
          rows={4}
          spanFull
        />
      </div>

      {readOnly && (
        <div className="mt-6 text-[12px] text-navy-dark/60 italic" data-testid="handbook-readonly-note">
          {de ? "Als Familienmitglied haben Sie Lesezugriff." : "As a family member you have read-only access."}
        </div>
      )}

      {showShare && (
        <ShareGuestDialog propertyId={selected.id} onClose={() => setShowShare(false)} />
      )}
    </div>
  );
}
